import { createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit';
import {
  CatalogProduct,
  CustomerProductPriceResult,
  ProductDetailTransfer,
  ProductListProduct,
  UnitOfMeasureOrderQuantity,
  USDAProductStatusTypeData,
} from '../../api';
import {
  ChatGreeting,
  ChatMessage,
  ChatPrompt,
  ChatResponseTypeEnum,
  InitChatGreetings,
} from '../../api/models/chat.models';
import { RootState } from '../store';

const messagesAdapter = createEntityAdapter<ChatMessage>({
  selectId: (data: ChatMessage) => data.id,
});

const productsAdapter = createEntityAdapter<CatalogProduct>({
  selectId: (product: CatalogProduct) => product.Id,
});

const promptsAdapter = createEntityAdapter<ChatPrompt>({
  selectId: (prompt: ChatPrompt) => prompt.ProductCatalogConversationPromptId,
});

interface ChatState {
  greetings: ChatGreeting[];
  prompts: EntityState<ChatPrompt>;
  isLoadingPrompts: boolean;
  products: EntityState<CatalogProduct>;
  messages: EntityState<ChatMessage>;
  threadId?: string;
  isLoading: boolean;
  visible: boolean;
}

const initialState: ChatState = {
  greetings: InitChatGreetings,
  prompts: promptsAdapter.getInitialState(),
  isLoadingPrompts: false,
  products: productsAdapter.getInitialState(),
  messages: messagesAdapter.getInitialState(),
  threadId: undefined,
  isLoading: false,
  visible: false,
};

// Reducers
export const chatSlice = createSlice({
  name: 'chat',
  initialState: initialState,
  reducers: {
    resetState: (state: ChatState) => initialState,
    setIsLoading: (state: ChatState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setGreetings: (state: ChatState, action: PayloadAction<ChatGreeting[]>) => {
      state.greetings = action.payload;
    },
    setPrompts: (state: ChatState, action: PayloadAction<ChatPrompt[]>) => {
      promptsAdapter.setAll(state.prompts, action.payload);
    },
    setIsLoadingPrompts: (state: ChatState, action: PayloadAction<boolean>) => {
      state.isLoadingPrompts = action.payload;
    },
    setThreadId: (state: ChatState, action: PayloadAction<string | undefined>) => {
      state.threadId = action.payload;
    },
    setChatMessage: (state: ChatState, action: PayloadAction<ChatMessage>) => {
      messagesAdapter.setOne(state.messages, action.payload);
    },
    updateChatMessage: (
      state: ChatState,
      action: PayloadAction<{ id: string; response: ChatResponseTypeEnum | undefined }>
    ) => {
      const msg = state.messages.entities[action.payload.id];
      if (msg) msg.response = action.payload.response;
    },
    setProducts: (state: ChatState, action: PayloadAction<CatalogProduct[]>) => {
      productsAdapter.setMany(state.products, action.payload);
    },
    updateProduct: (state: ChatState, action: PayloadAction<ProductDetailTransfer>) => {
      if (!action.payload.productKey) return;
      const product = state.products.entities[action.payload.productKey];
      if (product) {
        product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
          if (uom.UnitOfMeasure === action.payload.uom) {
            uom.ExtendedPrice = action.payload.extendedPrice;
            uom.Quantity = action.payload.quantity;
            uom.ShowOrderQuantityAlert = action.payload.showOrderQuantityAlert;
          }
        });
      }
    },
    setProductPrices: (state: ChatState, action: PayloadAction<CustomerProductPriceResult[]>) => {
      action.payload.forEach((priceResult: CustomerProductPriceResult) => {
        if (priceResult.ProductKey) {
          const product =
            state.products.entities[priceResult.ProductKey] ??
            state.products.entities[priceResult.ProductKey.toLowerCase()] ??
            state.products.entities[priceResult.ProductKey.toUpperCase()];
          if (product) {
            (product as ProductListProduct).PriceLoaded = true;
            product.UnitOfMeasureOrderQuantities.forEach((uom: UnitOfMeasureOrderQuantity) => {
              if (uom.UnitOfMeasure === priceResult.UnitOfMeasureType) {
                uom.Price = priceResult.Price;
                uom.SecondaryUnitPrice = priceResult.SecondaryUnitPrice;
                if (priceResult.ExtendedPrice) {
                  uom.ExtendedPrice = priceResult.ExtendedPrice;
                }
                if (priceResult.ProductAverageWeight > 0) {
                  uom.ProductAverageWeight = priceResult.ProductAverageWeight;
                }
              }
            });
          }
        }
      });
    },
    setUsdaProductStatusType: (state: ChatState, action: PayloadAction<USDAProductStatusTypeData[]>) => {
      action.payload.forEach((statusResult: USDAProductStatusTypeData) => {
        if (statusResult.ProductKey) {
          const product =
            state.products.entities[statusResult.ProductKey] ??
            state.products.entities[statusResult.ProductKey.toLowerCase()] ??
            state.products.entities[statusResult.ProductKey.toUpperCase()];
          if (product) {
            product.USDAProductStatusType = statusResult.USDAProductStatusType;
          }
        }
      });
    },
    setVisible: (state: ChatState, action: PayloadAction<boolean>) => {
      state.visible = action.payload;
    },
  },
});

export const { selectAll: selectAllChatProducts, selectById: selectChatProductById } = productsAdapter.getSelectors<
  EntityState<CatalogProduct>
>((state: EntityState<CatalogProduct>) => state);

export const { selectAll: selectAllChatMessages, selectById: selectChatMessageById } = messagesAdapter.getSelectors<
  EntityState<ChatMessage>
>((state: EntityState<ChatMessage>) => state);

export const { selectAll: selectAllChatPrompts } = promptsAdapter.getSelectors<RootState>((s) => s.chat.prompts);

export const selectRandomChatPrompts = createSelector([selectAllChatPrompts], (prompts) => {
  const result: ChatPrompt[] = [];

  for (let i = 0; i < prompts.length && i < 3; i++) {
    let randomPrompt: ChatPrompt | undefined = undefined;
    while (!randomPrompt || result.includes(randomPrompt)) {
      randomPrompt = prompts[Math.floor(Math.random() * prompts.length)];
    }
    result.push(randomPrompt);
  }

  return result;
});

export const selectChatUsdaProducts = createSelector(selectAllChatProducts, (products) => {
  return products.filter((p) => p.IsUSDAProduct).map((p) => p.ProductKey ?? '');
});
